import React, { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Box, Typography } from '@mui/material'
import { makeStyles } from '@mui/styles'
import {
  type BasePuiDialogProps,
  ButtonWithLoader,
  LanguageUtils,
  PuiDialog,
} from '@pbt/pbt-ui-components'

import { createPrice, editPrice } from '~/store/actions/procedures'
import { getCurrentBusinessIsOmniChannel } from '~/store/reducers/auth'
import { getCvcCumulusRhapsodyIntegrationProceduresEnabled } from '~/store/reducers/constants'
import {
  getAvailableSpeciesForProcedure,
  getProcedure,
  getProceduresIsLoading,
} from '~/store/reducers/procedures'
import type { BasePrice } from '~/types'
import useCloseAfterCreation from '~/utils/useCloseAfterCreation'

import PriceComponent, { type PriceHandle } from './Price'
import ReadOnlyPriceComponent, {
  type ReadOnlyPriceHandle,
} from './ReadOnlyPrice'

const useStyles = makeStyles(
  (theme) => ({
    dialogContentRoot: {
      padding: theme.spacing(3, 3, 4, 3),
    },
    paper: {
      width: 650,
      maxWidth: 650,
      [theme.breakpoints.down('md')]: {
        margin: '48px auto !important',
      },
      [theme.breakpoints.down('sm')]: {
        width: 'calc(100% - 32px)',
      },
    },
    button: {
      width: 144,
    },
    dialogTitleContainer: {
      display: 'flex',
      flexDirection: 'column' as const,
    },
    dialogSubtitle: {
      fontSize: theme.typography.body2.fontSize,
      color: theme.colors.secondaryText,
      marginTop: theme.spacing(0.5),
    },
  }),
  { name: 'PriceDialog' },
)

export interface PriceDialogProps extends BasePuiDialogProps {
  price?: BasePrice
  procedureId: string
}

const PriceDialog = ({
  price,
  procedureId,
  open,
  onClose,
}: PriceDialogProps) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { t } = useTranslation(['Common', 'Dialogs'])

  const procedure = useSelector(getProcedure(procedureId))
  const isLoading = useSelector(getProceduresIsLoading)
  const availableSpecies = useSelector(
    getAvailableSpeciesForProcedure(procedure?.id),
  )
  const isCvcCumulusRhapsodyIntegrationProceduresEnabled = useSelector(
    getCvcCumulusRhapsodyIntegrationProceduresEnabled,
  )

  const showInternalVariationNumber = useSelector(
    getCurrentBusinessIsOmniChannel,
  )

  const priceRef = useRef<PriceHandle | ReadOnlyPriceHandle>(null)
  const setCloseAfterCreationOn = useCloseAfterCreation(
    onClose,
    getProceduresIsLoading,
  )

  const save = () => {
    if (priceRef.current?.validate()) {
      const newPrice = priceRef.current?.get()
      setCloseAfterCreationOn()
      if (!price) {
        dispatch(createPrice(procedureId, newPrice))
      } else {
        dispatch(editPrice(procedureId, { ...newPrice, id: price.id }))
      }
    }
  }

  const hasUnsavedChanges = () => priceRef.current?.hasUnsavedChanges() ?? false

  const internalVariationNumber = procedure?.cumulusId

  const dialogTitle = (
    <Box className={classes.dialogTitleContainer}>
      <Typography component="div" variant="h6">
        {t('Dialogs:PRICE_DIALOG.ADD_PRICE_FOR_PROCEDURE', {
          procedureName: LanguageUtils.getTranslatedFieldName(procedure),
        })}
      </Typography>
      {showInternalVariationNumber && internalVariationNumber && (
        <Typography className={classes.dialogSubtitle}>
          {t('Common:INTERNAL_VARIATION_NUMBER')} {internalVariationNumber}
        </Typography>
      )}
    </Box>
  )

  return (
    <PuiDialog
      confirmSaveOnClose
      fullWidth
      ConfirmCloseDialogProps={{
        onOk: save,
      }}
      actions={
        <ButtonWithLoader
          className={classes.button}
          disabled={isLoading}
          loading={isLoading}
          type="submit"
          onClick={save}
        >
          {t('Common:SAVE_ACTION')}
        </ButtonWithLoader>
      }
      aria-labelledby="price-dialog"
      classes={{
        paper: classes.paper,
        dialogContentRoot: classes.dialogContentRoot,
      }}
      hasUnsavedChanges={hasUnsavedChanges}
      maxWidth="sm"
      open={open}
      title={dialogTitle}
      onClose={onClose}
    >
      {isCvcCumulusRhapsodyIntegrationProceduresEnabled && price ? (
        <ReadOnlyPriceComponent
          availableSpecies={availableSpecies}
          price={price}
          ref={priceRef}
        />
      ) : (
        <PriceComponent
          availableSpecies={availableSpecies}
          price={price}
          ref={priceRef}
        />
      )}
    </PuiDialog>
  )
}

export default PriceDialog
